import React, { useState } from 'react';

import { ArrowDownSLine } from '@assets/icons';
import { useBreakpoint } from '@contexts/breakpoints';
import { formatValidTourDate } from '@components/views/tour/components/tour-dates/utils';
import CustomIcon from '@components/utils/CustomIcon';
import { getCurrencySymbol } from '@utils/utils';

import { getPriceStartingFromLabel } from './discountHelpers';
import { PackageTemplateSelectionProps } from './types';
import PackageTemplateResponsiveDropdown from './PackageTemplateResponsiveDropdown';
import { PackageSearchInfoSkeleton } from './PackageTemplateSkeleton';
import {
  isPersonQuotaUnavailable,
  shouldShowRemainingPersonQuotaBadge,
} from './guestLimits';
import {
  DropdownSurface,
  HighlightBullet,
  HighlightItem,
  HighlightText,
  HighlightsList,
  HighlightsShowMoreButton,
  MetaBlock,
  MobileSelectionButton,
  MobileSelectionList,
  Placeholder,
  SelectionButton,
  SelectionChevron,
  SelectionContentStack,
  SelectionDate,
  DropdownPackageTitle,
  SelectionList,
  SelectionMeta,
  DropdownRemainingQuotaBadge,
  OccupiedBadge,
  SelectableItemContent,
  SelectionPriceBlock,
  SelectionPriceNote,
  SearchInfoDetails,
  SearchInfoCard,
  SearchInfoPanel,
  SearchInfoTitle,
  SearchLoadingControls,
  SearchDepartureItem,
  SearchDepartureList,
  SearchDepartureName,
  SearchDepartureTitle,
  SelectionTrigger,
  SelectionTriggerPriceRow,
  SearchControls,
} from './styled';

const VISIBLE_HIGHLIGHTS_LIMIT = 5;

type HighlightsProps = {
  fixedHighlights: string[];
  features: string[];
  resetKey: string;
};

const Highlights = ({
  fixedHighlights,
  features,
  resetKey,
  isMobile,
}: HighlightsProps & { isMobile: boolean }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleFeatures =
    !isMobile || isExpanded
      ? features
      : features.slice(0, VISIBLE_HIGHLIGHTS_LIMIT);
  const hasMoreFeatures =
    isMobile && features.length > VISIBLE_HIGHLIGHTS_LIMIT;
  const visibleHighlights = [...fixedHighlights, ...visibleFeatures];

  return (
    <div key={resetKey}>
      <HighlightsList>
        {visibleHighlights.map((highlight, index) => (
          <HighlightItem key={`${highlight}-${index}`}>
            <HighlightBullet />
            <HighlightText>{highlight}</HighlightText>
          </HighlightItem>
        ))}
      </HighlightsList>
      {hasMoreFeatures ? (
        <HighlightsShowMoreButton
          type='button'
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((current) => !current)}
        >
          {isExpanded ? 'Daha az göster' : 'Daha fazla göster'}
        </HighlightsShowMoreButton>
      ) : null}
    </div>
  );
};

const formatPrice = (price?: number) => {
  if (!price) return { main: '0', decimal: ',00' };

  const priceStr = price.toFixed(2);
  const [main, decimal] = priceStr.split('.');

  return { main, decimal: `,${decimal}` };
};

const PackageSearchInfoPanel = ({
  children,
  isLoading = false,
  packages,
  selectedPackageId,
  selectedPackageDetailId,
  selectedPackageSelectionDetailId,
  precalculatedPrice,
  precalculatedCurrencyCode,
  hidePrecalculatedPrice = false,
  dayCount,
  nightCount,
  packageDetail,
  selectedPackageAvailability,
  onPackageChange,
}: PackageTemplateSelectionProps) => {
  const { xs: isMobile } = useBreakpoint();
  const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false);
  const selectedDateDetailId =
    selectedPackageSelectionDetailId ?? selectedPackageDetailId;
  const isSelectedPackage = (packageId: number, detailId?: number | null) =>
    packageId === selectedPackageId &&
    (detailId ?? undefined) === selectedDateDetailId;
  const selectedPackage =
    packages.find((item) => isSelectedPackage(item.id, item.detailId)) ||
    packages.find((item) => item.id === selectedPackageId) ||
    packages[0];
  const selectedCurrencyCode =
    precalculatedCurrencyCode ||
    packageDetail?.currencyCode ||
    selectedPackage?.currency ||
    'TL';
  const selectedCurrencySymbol = getCurrencySymbol(selectedCurrencyCode);
  const departureDate = formatValidTourDate(
    packageDetail?.defaultPackageDetail?.departureDate
  );
  const arrivalDate = formatValidTourDate(
    packageDetail?.defaultPackageDetail?.arrivalDate
  );
  const dateLabel = [departureDate, arrivalDate].filter(Boolean).join(' - ');
  const departurePoints =
    packageDetail?.defaultPackageDetail?.departurePoints || [];
  const fixedHighlights = [
    ...(typeof nightCount === 'number' && typeof dayCount === 'number'
      ? [`${nightCount} Gece, ${dayCount} Gün`]
      : []),
  ];
  const features =
    packageDetail?.features
      ?.map((item) => item.name?.trim())
      .filter((name): name is string => Boolean(name)) || [];
  const highlightsResetKey = `${selectedPackageId ?? 'default'}-${
    selectedDateDetailId ?? 'default'
  }`;

  const selectedPackageAvailabilityInput =
    selectedPackageAvailability || selectedPackage;
  const isSelectedPackageUnavailable = isPersonQuotaUnavailable(
    selectedPackageAvailabilityInput || {}
  );
  const selectedPrice =
    hidePrecalculatedPrice || isSelectedPackageUnavailable
      ? undefined
      : precalculatedPrice;
  const renderPackageOptions = ({
    mobile,
    width = 0,
  }: {
    mobile: boolean;
    width?: number;
  }) => {
    const List = mobile ? MobileSelectionList : SelectionList;
    const OptionButton = mobile ? MobileSelectionButton : SelectionButton;
    const options = (
      <List role='listbox'>
        {packages.map((item, index) => {
          const isSelected = isSelectedPackage(item.id, item.detailId);
          const packageAvailabilityInput =
            isSelected && selectedPackageAvailability
              ? selectedPackageAvailability
              : item;
          const optionPrice =
            isSelected && !hidePrecalculatedPrice
              ? selectedPrice ?? item.price
              : item.price;
          const optionCurrencyCode =
            item.currency || (isSelected ? selectedCurrencyCode : 'TL');
          const optionCurrencySymbol = getCurrencySymbol(optionCurrencyCode);

          return (
            <OptionButton
              key={`${item.id}-${item.detailId ?? 'default'}-${index}`}
              type='button'
              role='option'
              aria-selected={isSelected}
              $selected={isSelected}
              onClick={() => {
                onPackageChange?.(item.id, item.detailId);
                setIsPackageDropdownOpen(false);
              }}
            >
              <SelectableItemContent>
                <DropdownPackageTitle>
                  {item.name || 'Paket'}
                </DropdownPackageTitle>
                {shouldShowRemainingPersonQuotaBadge(
                  packageAvailabilityInput
                ) ? (
                  <DropdownRemainingQuotaBadge>
                    Son {item.remainingPersonQuota} kişi
                  </DropdownRemainingQuotaBadge>
                ) : null}
                {isPersonQuotaUnavailable(packageAvailabilityInput) ? (
                  <OccupiedBadge>Müsaitlik bulunmamaktadır.</OccupiedBadge>
                ) : null}
              </SelectableItemContent>
              {optionPrice !== undefined &&
              optionPrice !== null &&
              !isPersonQuotaUnavailable(packageAvailabilityInput) ? (
                <SelectionPriceBlock>
                  <SelectionMeta>
                    {formatPrice(optionPrice).main}
                    {formatPrice(optionPrice).decimal} {optionCurrencySymbol}
                  </SelectionMeta>
                  <SelectionPriceNote>
                    {getPriceStartingFromLabel(optionCurrencyCode)}
                  </SelectionPriceNote>
                </SelectionPriceBlock>
              ) : null}
            </OptionButton>
          );
        })}
      </List>
    );

    if (mobile) return options;

    return <DropdownSurface $width={width}>{options}</DropdownSurface>;
  };

  const packageSelector = (
    <PackageTemplateResponsiveDropdown
      isOpen={isPackageDropdownOpen}
      title='Tarih Seçimi'
      onClose={() => setIsPackageDropdownOpen(false)}
      renderContent={renderPackageOptions}
      trigger={
        <SelectionTrigger
          type='button'
          $selected
          aria-haspopup='listbox'
          aria-expanded={isPackageDropdownOpen}
          disabled={packages.length < 2}
          onClick={() => setIsPackageDropdownOpen((current) => !current)}
        >
          <SelectionContentStack>
            <SelectionDate>
              {dateLabel || selectedPackage?.name || 'Paket'}
            </SelectionDate>
            {selectedPrice !== undefined && selectedPrice !== null ? (
              <SelectionTriggerPriceRow>
                <SelectionMeta>
                  {formatPrice(selectedPrice).main}
                  {formatPrice(selectedPrice).decimal} {selectedCurrencySymbol}
                </SelectionMeta>
              </SelectionTriggerPriceRow>
            ) : null}
          </SelectionContentStack>
          {packages.length > 1 ? (
            <SelectionChevron aria-hidden='true'>
              <CustomIcon
                icon={ArrowDownSLine}
                fontSize={20}
                paletteColor='grey700'
              />
            </SelectionChevron>
          ) : null}
        </SelectionTrigger>
      }
    />
  );

  return (
    <SearchInfoPanel>
      <SearchInfoTitle>Tur Arama Bilgileri</SearchInfoTitle>
      <SearchInfoCard>
        {packages.length === 0 ? (
          <Placeholder>Paket verisi bulunamadı.</Placeholder>
        ) : (
          <>
            {packageSelector}

            {isLoading ? (
              <SearchLoadingControls data-testid='package-loading-controls'>
                <PackageSearchInfoSkeleton />
              </SearchLoadingControls>
            ) : (
              <>
                {children ? <SearchControls>{children}</SearchControls> : null}

                {fixedHighlights.length > 0 ||
                features.length > 0 ||
                departurePoints.length > 0 ? (
                  <SearchInfoDetails>
                    {fixedHighlights.length > 0 || features.length > 0 ? (
                      <Highlights
                        key={highlightsResetKey}
                        fixedHighlights={fixedHighlights}
                        features={features}
                        resetKey={highlightsResetKey}
                        isMobile={isMobile}
                      />
                    ) : null}

                    {departurePoints.length > 0 ? (
                      <MetaBlock>
                        <SearchDepartureTitle>
                          Kalkış Noktaları
                        </SearchDepartureTitle>
                        <SearchDepartureList>
                          {departurePoints.map((point) => (
                            <SearchDepartureItem key={point.id}>
                              <SearchDepartureName>
                                {point.name || point.description}
                              </SearchDepartureName>
                            </SearchDepartureItem>
                          ))}
                        </SearchDepartureList>
                      </MetaBlock>
                    ) : null}
                  </SearchInfoDetails>
                ) : null}
              </>
            )}
          </>
        )}
      </SearchInfoCard>
    </SearchInfoPanel>
  );
};

export default PackageSearchInfoPanel;
